import { useState, useEffect } from "react";
import { api, apiBase, setApiBase } from "../api";
import Icon from "../components/Icon";
import Badge from "../components/Badge";
import type { ClientRow } from "../types";

type DashboardProps = {
  onPickClient: (id: string) => void;
};

type Counts = { total: number; published: number; draft: number };

export default function Dashboard({ onPickClient }: DashboardProps) {
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [counts, setCounts] = useState<Record<string, Counts>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [urlDraft, setUrlDraft] = useState(apiBase());

  useEffect(() => {
    if (apiBase()) void load();
  }, []);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const data = await api.getClients();
      setClients(data.clients);
      void loadCounts(data.clients);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  // Per-client asset counts, fetched in parallel. A failure for one client
  // leaves that client without counts (cards fall back to "—"); it never
  // breaks the dashboard.
  async function loadCounts(list: ClientRow[]) {
    const entries = await Promise.all(
      list.map(async (c): Promise<[string, Counts] | null> => {
        try {
          const { assets } = await api.getAssets(c.id);
          return [
            c.id,
            {
              total: assets.length,
              published: assets.filter((a) => a.publication_state === "published").length,
              draft: assets.filter((a) => a.publication_state === "draft").length,
            },
          ];
        } catch {
          return null;
        }
      })
    );
    const map: Record<string, Counts> = {};
    for (const e of entries) if (e) map[e[0]] = e[1];
    setCounts(map);
  }

  function saveUrl() {
    setApiBase(urlDraft.trim());
    void load();
  }

  if (!apiBase()) {
    return (
      <div className="page page-enter" style={{ maxWidth: 520, margin: "0 auto", paddingTop: 64 }}>
        <span className="eyebrow">Setup</span>
        <h2 className="t-h2" style={{ marginTop: 12, marginBottom: 8 }}>Connect to your API.</h2>
        <p className="t-body" style={{ color: "var(--fg-muted)", marginBottom: 28 }}>
          Enter the BgSPIM API Worker URL to get started.
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            className="input"
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && saveUrl()}
            placeholder="http://127.0.0.1:8787"
            autoFocus
          />
          <button className="btn btn-primary" onClick={saveUrl}>Connect</button>
        </div>
      </div>
    );
  }

  const haveCounts = Object.keys(counts).length > 0;
  const agg = Object.values(counts).reduce(
    (a, c) => ({ total: a.total + c.total, published: a.published + c.published, draft: a.draft + c.draft }),
    { total: 0, published: 0, draft: 0 }
  );

  return (
    <div className="page page-enter">
      <div style={{ maxWidth: 1240, margin: "0 auto" }}>
        <div style={{ marginBottom: 28 }}>
          <span className="eyebrow">Multi-client workspace</span>
          <h1 className="t-h1" style={{ marginTop: 14, marginBottom: 10, fontSize: 44 }}>
            {loading
              ? "Loading clients…"
              : clients.length > 0
              ? `${clients.length} client${clients.length === 1 ? "" : "s"} to manage.`
              : "No clients found."}
          </h1>
          {!loading && clients.length > 0 && (
            <p className="t-body" style={{ color: "var(--fg-muted)", fontSize: 17, maxWidth: 640, lineHeight: 1.6 }}>
              Pick a client to enter their catalog.
            </p>
          )}
        </div>

        {/* Aggregate stats (real data, summed across clients) */}
        {!loading && clients.length > 0 && haveCounts && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 16, marginBottom: 28 }}>
            <StatCard label="Clients" value={clients.length} />
            <StatCard label="Published assets" value={agg.published} />
            <StatCard label="Draft assets" value={agg.draft} />
          </div>
        )}

        {error && (
          <div
            role="alert"
            style={{
              padding: "12px 16px",
              background: "rgba(229,138,109,0.10)",
              border: "1px solid rgba(229,138,109,0.35)",
              borderRadius: "var(--r-md)",
              color: "#e58a6d",
              marginBottom: 24,
              fontFamily: "var(--font-mono)",
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Icon name="x" size={14} />
            {error}
          </div>
        )}

        {loading && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20 }}>
            {[1, 2, 3].map((i) => (
              <div key={i} className="client-card" style={{ minHeight: 240 }}>
                <div className="hero skeleton" style={{ aspectRatio: "16/9" }} />
                <div className="body">
                  <div className="skeleton" style={{ height: 24, width: "60%", marginBottom: 8, borderRadius: 4 }} />
                  <div className="skeleton" style={{ height: 14, width: "40%", borderRadius: 4 }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && clients.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20 }}>
            {clients.map((c) => (
              <ClientCard key={c.id} client={c} counts={counts[c.id]} onPick={() => onPickClient(c.id)} />
            ))}
          </div>
        )}

        {!loading && !error && clients.length === 0 && (
          <div className="empty card" style={{ padding: "80px 24px" }}>
            <div className="glyph"><Icon name="box" size={22} /></div>
            <h4 className="t-h4">No clients in this workspace.</h4>
            <p style={{ marginTop: 8 }}>The API returned an empty client list. Check the worker or seed the database.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="card card-pad">
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--fg-muted)" }}>
        {label}
      </div>
      <div style={{ fontFamily: "var(--font-serif)", fontSize: 40, fontWeight: 650, marginTop: 6, fontVariationSettings: '"opsz" 60, "SOFT" 30' }}>
        {value}
      </div>
    </div>
  );
}

function ClientCard({ client, counts, onPick }: { client: ClientRow; counts?: Counts; onPick: () => void }) {
  return (
    <button className="client-card" onClick={onPick}>
      <div className="hero">
        <ClientHeroPlaceholder mark={client.slug.slice(0, 2).toUpperCase()} />
        {client.status && client.status !== "active" && (
          <div style={{ position: "absolute", top: 14, right: 16 }}>
            <Badge kind="draft">{client.status}</Badge>
          </div>
        )}
      </div>
      <div className="body" style={{ textAlign: "left" }}>
        <div className="name">{client.name}</div>
        <div className="dom">{client.slug}</div>
        <div
          className="stats"
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: "1px solid var(--rule-faint)",
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 8,
          }}
        >
          <CardStat label="assets" value={counts ? counts.total : "—"} />
          <CardStat label="published" value={counts ? counts.published : "—"} />
          <CardStat label="draft" value={counts ? counts.draft : "—"} />
        </div>
      </div>
    </button>
  );
}

function CardStat({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 16, color: "var(--fg)" }}>{value}</div>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--fg-muted)", letterSpacing: "0.06em", textTransform: "uppercase", marginTop: 2 }}>
        {label}
      </div>
    </div>
  );
}

function ClientHeroPlaceholder({ mark }: { mark: string }) {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        background: "linear-gradient(135deg, #16212e 0%, #0a1024 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-serif)",
          fontSize: 60,
          color: "rgba(244,196,90,0.18)",
          fontWeight: 700,
          letterSpacing: "-0.04em",
        }}
      >
        {mark}
      </div>
    </div>
  );
}
